import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { validateEstimateForm } from '../utils/formValidation';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SERVICES = [
  'Kitchen Remodeling',
  'Bathroom Remodeling',
  'Deck Building / Trex Decking',
  'Basement Finishing',
  'Vinyl Siding',
  'Window Replacement',
  'Custom Furniture & Aquariums',
  'Home Maintenance',
  'Other'
];

const EstimateFloater = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', city: '',
    service_interest: '', message: '', sms_opt_in: false, website: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    setFieldErrors({});

    const { valid, errors } = validateEstimateForm(formData);
    if (!valid) {
      setFieldErrors(errors);
      setStatus({ type: 'error', message: 'Please fix the errors below.' });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/estimate`, formData, { timeout: 15000 });
      if (res.data.success) {
        setStatus({ type: 'success', message: 'Thank you! We\'ll get back to you shortly.' });
        setFormData({ name: '', email: '', phone: '', address: '', city: '', service_interest: '', message: '', sms_opt_in: false, website: '' });
        setFieldErrors({});
      } else {
        setStatus({ type: 'error', message: res.data.error || 'Please check your information and try again.' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: err.response?.data?.error || err.response?.data?.detail || 'Something went wrong. Please call us at (203) 604-3202.' });
    } finally { setLoading(false); }
  };

  return (
    <>
      {/* Floating Button - pushed up on mobile to avoid MobileStickyBar */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 lg:bottom-4 z-40 bg-[#3a87fe] text-white pl-4 pr-5 py-3 rounded-lg text-sm font-semibold shadow-lg hover:bg-[#2968d9] transition-colors duration-200 flex items-center gap-2"
        data-testid="estimate-floater"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Get Free Estimate
      </button>

      {/* Modal Overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="estimate-modal-overlay">
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid="estimate-modal"
          >
            {/* Header */}
            <div className="bg-[#5c0701] text-white px-6 py-4 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
              <div>
                <h2 className="text-lg font-bold">Get Your Free Estimate</h2>
                <p className="text-xs opacity-80">Licensed & Insured CT Contractor</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white text-2xl leading-none" data-testid="estimate-modal-close">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="estimate-modal-form">
              {/* Honeypot — hidden from real users, bots fill it */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                <label htmlFor="modal-website">Website</label>
                <input type="text" id="modal-website" name="website" value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
              </div>

              {status.message && (
                <div className={`p-3 rounded-lg text-sm ${status.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
                  {status.message}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-gray-700 font-medium mb-1 text-sm">Full Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required
                  placeholder="Your full name"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition ${fieldErrors.name ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                  data-testid="modal-name" />
                {fieldErrors.name && <p className="text-red-500 text-xs mt-1">{fieldErrors.name}</p>}
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-sm">Email *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required
                    placeholder="your@email.com"
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition ${fieldErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    data-testid="modal-email" />
                  {fieldErrors.email && <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>}
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-sm">Phone *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                    placeholder="(203) 555-1234"
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition ${fieldErrors.phone ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    data-testid="modal-phone" />
                  {fieldErrors.phone && <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>}
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-gray-700 font-medium mb-1 text-sm">Property Address</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange}
                  placeholder="123 Main St"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition"
                  data-testid="modal-address" />
              </div>

              {/* City & Service */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-sm">City</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange}
                    placeholder="Meriden, CT"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition"
                    data-testid="modal-city" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-sm">Service</label>
                  <select name="service_interest" value={formData.service_interest} onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent transition bg-white"
                    data-testid="modal-service">
                    <option value="">Select a service</option>
                    {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-gray-700 font-medium mb-1 text-sm">Project Details *</label>
                <textarea name="message" value={formData.message} onChange={handleChange} required rows="3"
                  placeholder="Tell us about your project..."
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent resize-none transition ${fieldErrors.message ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                  data-testid="modal-message"></textarea>
                {fieldErrors.message && <p className="text-red-500 text-xs mt-1">{fieldErrors.message}</p>}
              </div>

              {/* SMS Opt-in */}
              <label className="flex items-start gap-2.5 cursor-pointer p-3 bg-gray-50 rounded-lg border border-gray-200">
                <input type="checkbox" name="sms_opt_in" checked={formData.sms_opt_in} onChange={handleChange}
                  className="mt-0.5 w-4 h-4 text-[#3a87fe] rounded border-gray-300 focus:ring-[#3a87fe]"
                  data-testid="modal-sms-optin" />
                <span className="text-xs text-gray-500 leading-relaxed">
                  I agree to receive SMS from Visser General Construction LLC. Msg & data rates may apply. Reply STOP to unsubscribe. See{' '}
                  <Link to="/terms" className="text-[#3a87fe] underline">Terms</Link> &{' '}
                  <Link to="/privacy" className="text-[#3a87fe] underline">Privacy</Link>.
                </span>
              </label>

              {/* Submit */}
              <button type="submit" disabled={loading}
                className={`w-full bg-[#5c0701] text-white py-3 rounded-lg font-semibold transition-colors duration-200 ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#7a1a0e]'}`}
                data-testid="modal-submit">
                {loading ? 'Sending...' : 'Get My Free Estimate'}
              </button>

              <p className="text-center">
                <a href="tel:2036043202" className="text-[#3a87fe] text-sm font-medium hover:underline">Or call us: (203) 604-3202</a>
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default EstimateFloater;
